const EVENTS = {
  1: 'PLUS PAGE VIEW',
  21: 'FREE TRIAL REGISTRATION',
  22: 'PROFILE COMPLETE',
  23: 'WEBSITE LAUNCHED',
  24: 'SOCIAL BRANDING',
  25: 'MARKETING SET UP',
  26: 'PAID HJ PLUS',
  27: 'UNIVERSITY',
  28: 'BILLING INFO ADDED',
  29: 'UPGRADE',
  30: 'HJ PLUS REJECTED'
};

/**
 * Trigger an event to be tracked in the vision reporting system
 * 
 * @param {string|number} eventName - Event name or ID to track
 * @param {string} source - Source of the event
 * @param {object} eventMeta - Additional metadata for the event
 * @param {number|null} agentId - Optional agent ID (will use global if available)
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
async function triggerEvent(eventName, source=null, eventMeta = {}, entityId = null) {
  try {
        // Get agent ID from global variable if not provided
        if (!entityId) {
          console.error('Admin ID is required');
          return { success: false, message: 'Admin ID is required' };
      }
        // Validate required parameters
        if (!eventName) {
            console.error('Event name or ID is required');
            return { success: false, message: 'Event name or ID is required' };
        }

        // Prepare payload with the format expected by the PHP controller
        const payload = {
            event_meta: eventMeta || {}
        };

        if(source){
          payload.source = source;
        }
        // Add event_id or event_name based on the type
        if (typeof eventName === 'number') {
            payload.event_id = eventName;
        } else if (typeof eventName === 'string') {
            if (!isNaN(parseInt(eventName))) {
                payload.event_id = parseInt(eventName);
            } else {
                payload.event_name = eventName;
            }
        }
        
        // Add agent_id if available
        if (entityId) {
            payload.entity_id = entityId;
        }

        // Send the tracking request to our PHP endpoint
        const response = await fetch('/api/v1/event-log/tevnt', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
  } catch (error) {
        console.error('Error triggering event:', error);
        return { success: false, message: error.message };
  }
}

/**
 * Track page conversion event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackConversionOnPage(entityId,meta = {}) {
  return triggerEvent(EVENTS[1], 'page_visit', meta, entityId);
}

/**
 * Track HJ+ trial started event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackHJPlusTrialStarted(entityId,meta = {}) {
  return triggerEvent(EVENTS[21], 'precheck', meta, entityId);
}

/**
 * Track profile completed event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackProfileCompleted(entityId,meta = {}) {
  return triggerEvent(EVENTS[22], 'agent_profile', meta, entityId);
}

/**
 * Track website launched event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackWebsiteLaunched(entityId,meta = {}) {
  return triggerEvent(EVENTS[23], 'agent_website', meta, entityId);
}

/**
 * Track social branding generated event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackSocialBrandingGenerated(entityId,meta = {}) {
  return triggerEvent(EVENTS[24], 'social_branding', meta, entityId);
}

/**
 * Track marketing setup completed event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackMarketingSetupCompleted(entityId,meta = {}) {
  return triggerEvent(EVENTS[25], 'marketing_wizard', meta, entityId);
}

/**
 * Track paid HJ+ event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackPaidHJPlus(entityId,meta = {}) {
  return triggerEvent(EVENTS[26], 'billing', meta, entityId);
}

/**
 * Track university completed event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackUniversityCompleted(entityId,meta = {}) {
  return triggerEvent(EVENTS[27], 'university', meta, entityId);
}

/**
 * Track credit card info added event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackCreditCardInfoAdded(entityId,meta = {}) {
  return triggerEvent(EVENTS[28], 'billing_profile', meta, entityId);
}

/**
 * Track PPC upgrade event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackPPCUpgrade(entityId,meta = {}) {
  return triggerEvent(EVENTS[29], 'ppc_register', meta, entityId);
}

/**
 * Track HJ+ trial rejected event
 * @param {object} meta - Additional metadata
 * @returns {Promise<object>} - Promise resolving to the tracking response
 */
export function trackHJPlusTrialRejected(entityId,meta = {}) {
  return triggerEvent(EVENTS[30], 'trial_popup', meta, entityId);
}

// Export the main function as default
export default triggerEvent;

// Export the event constants for direct use
export { EVENTS };